import { debug } from 'console';
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  sub: string;
  jti: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name': string;
  roles: string[];
  permissions: string[];
  AdminName: string;
  AdminId: string;
  exp: number;
  iss: string;
  aud: string;
  ProfilePicture: string;
}

export const getToken = (): string | null => localStorage.getItem('token');

const decodeToken = (): JwtPayload | null => {
  const token = getToken();
  if (token) {
    return jwtDecode<JwtPayload>(token);
  }

  return null;
};

export const getUserName = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
    : null;
};

export const getUserEmail = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
      ]
    : null;
};

export const getUserId = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken
    ? decodedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
      ]
    : null;
};

export const getAdminId = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken ? decodedToken['AdminId'] : null;
};

export const getAdminUserName = (): string | null => {
  const decodedToken = decodeToken();

  return decodedToken ? decodedToken['AdminName'] : null;
};

export const getProfilePicture = (): string | null => {
  // First, try to get the profile picture from localStorage
  const profilePicture = localStorage.getItem('profilePicture');

  // If found in localStorage, return it
  if (profilePicture) {
    return profilePicture;
  }

  // Otherwise, decode the token and get the profile picture from claims
  const decodedToken = decodeToken();
  if (decodedToken && decodedToken.ProfilePicture) {
    // Store the profile picture in localStorage for future use
    localStorage.setItem('profilePicture', decodedToken.ProfilePicture);

    // Return the profile picture from the token
    return decodedToken.ProfilePicture;
  }

  // If nothing found, return null
  return null;
};

export const getRoles = (): string[] | null => {
  const decodedToken = decodeToken();
  if (decodedToken) {
    const roles = decodedToken.roles;

    if (!roles) {
      return null;
    }

    // Handle case where roles might be stringified JSON
    if (typeof roles === 'string') {
      try {
        // Try to parse if it's a stringified array
        const parsedRoles = JSON.parse(roles);
        return Array.isArray(parsedRoles) ? parsedRoles : [roles];
      } catch {
        // If parsing fails, treat it as a single role string
        return [roles];
      }
    }

    // If roles is already an array, return it
    if (Array.isArray(roles)) {
      return roles;
    }

    // If roles is neither a string nor an array, return it as a single-item array
    return [String(roles)];
  }

  return null;
};

export const getPermissions = (): string[] | null => {
  const decodedToken = decodeToken();
  if (decodedToken) {
    const permissions = decodedToken.permissions;

    if (!permissions) {
      return null;
    }

    // Handle case where permissions might be stringified JSON
    if (typeof permissions === 'string') {
      try {
        // Try to parse if it's a stringified array
        const parsedPermissions = JSON.parse(permissions);
        return Array.isArray(parsedPermissions) ? parsedPermissions : [permissions];
      } catch {
        // If parsing fails, treat it as a single permission string
        return [permissions];
      }
    }

    // If permissions is already an array, return it
    if (Array.isArray(permissions)) {
      return permissions;
    }

    // If permissions is neither a string nor an array, return it as a single-item array
    return [String(permissions)];
  }

  return null;
};
